import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup
} from '@/components/ui/dropdown-menu';
import { Globe, Menu, X, ChevronDown, Phone, Calculator, Truck, Leaf, MapPin, Clock, User, LogOut, LogIn, FileText } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import grassDepotLogo from '@/assets/grass-depot-logo.png';

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const { user, signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
    const currentPath = window.location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/(en|es)/, '');
    navigate(`/${newLang}${pathWithoutLang}`);
  };

  const menuItems = [
    { key: 'home', label: t('nav.home'), href: `/${language}` },
    {
      key: 'products',
      label: language === 'en' ? 'Products' : 'Productos',
      href: language === 'en' ? `/${language}/products` : `/${language}/productos`,
      submenu: [
        {
          key: 'grass',
          label: t('nav.grass'),
          href: `/${language}/grass`,
          subItems: ['St. Augustine Raleigh', 'St. Augustine Palmetto', 'Bermuda Tifway 419', 'Zoysia Palisades', 'Zoysia Emerald', 'Bahia', 'Carpet Grass']
        },
        {
          key: 'rock',
          label: t('nav.rock'),
          href: `/${language}/rock`,
          subItems: ['Bull Rock', 'River Rock', 'Pea Gravel', 'Limestone', 'Granite']
        },
        {
          key: 'mulch',
          label: t('nav.mulch'),
          href: `/${language}/mulch`,
          subItems: ['Black Mulch', 'Brown Mulch', 'Red Mulch', 'Hardwood Mulch']
        },
        {
          key: 'soil',
          label: t('nav.soil'),
          href: `/${language}/soil`,
          subItems: ['Top Soil', 'Garden Soil', 'Compost']
        },
        {
          key: 'sand',
          label: t('nav.sand'),
          href: `/${language}/sand`,
          subItems: ['Mason Sand', 'Play Sand', 'Concrete Sand']
        }
      ]
    },
    {
      key: 'services',
      label: t('nav.services'),
      href: `/${language}/services`,
      submenu: [
        { 
          key: 'sod-delivery', 
          label: language === 'en' ? 'Sod Delivery' : 'Entrega de Césped', 
          href: language === 'en' ? `/${language}/services/sod-delivery` : `/${language}/servicios/entrega-cesped`
        },
        { 
          key: 'bulk-delivery', 
          label: language === 'en' ? 'Bulk Delivery' : 'Entrega a Granel', 
          href: language === 'en' ? `/${language}/services/bulk-delivery` : `/${language}/servicios/entrega-granel`
        }
      ]
    },
    { key: 'areas', label: language === 'en' ? 'Areas We Serve' : 'Áreas que Servimos', href: `/${language}/areas` },
    { key: 'blog', label: 'Blog', href: `/${language}/blog` },
    { key: 'about', label: t('nav.about'), href: `/${language}/about` },
    { key: 'contact', label: t('nav.contact'), href: `/${language}/contact` }
  ];

  return (
    <header className="bg-background/95 backdrop-blur-sm border-b border-border sticky top-0 z-50 shadow-card">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={`/${language}`} className="flex items-center hover:opacity-90 transition-opacity">
            <img 
              src={grassDepotLogo} 
              alt="The Grass Depot LLC"
              className="h-10 md:h-12 w-auto"
              width="68"
              height="48"
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {menuItems.map((item) => (
              <div key={item.key} className="relative">
                {item.submenu ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="flex items-center text-foreground hover:text-primary transition-colors py-2 px-3 h-auto"
                      >
                        {item.label}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      className="w-80 bg-background/95 backdrop-blur-sm border border-border shadow-glow z-[60]"
                      align="start"
                      sideOffset={8}
                    >
                      {item.key === 'products' ? (
                        <>
                          <DropdownMenuLabel className="text-primary font-semibold">
                            {language === 'en' ? 'Our Products' : 'Nuestros Productos'}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {item.submenu.map((category: any, index: number) => (
                            <div key={category.key}>
                              <DropdownMenuGroup>
                                <DropdownMenuItem asChild>
                                  <Link
                                    to={category.href}
                                    className="flex items-center justify-between px-3 py-3 text-sm font-medium cursor-pointer"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className="w-8 h-8 bg-gradient-natural rounded-lg flex items-center justify-center">
                                        <Leaf className="h-4 w-4 text-white" />
                                      </div>
                                      <div>
                                        <div className="font-medium">{category.label}</div>
                                        <div className="text-xs text-muted-foreground">
                                          {category.subItems?.length || 0} {language === 'en' ? 'varieties' : 'variedades'}
                                        </div>
                                      </div>
                                    </div>
                                    <ChevronDown className="h-4 w-4 opacity-60" />
                                  </Link>
                                </DropdownMenuItem>
                                {category.subItems && (
                                  <div className="pl-6 pr-3 pb-2 space-y-1">
                                    {category.subItems.slice(0, 3).map((product: string) => (
                                      <DropdownMenuItem key={product} asChild>
                                        <Link
                                          to={`${category.href}/${product.toLowerCase().replace(/\s+/g, '-')}`}
                                          className="block px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                                        >
                                          • {product}
                                        </Link>
                                      </DropdownMenuItem>
                                    ))}
                                    {category.subItems.length > 3 && (
                                      <DropdownMenuItem asChild>
                                        <Link
                                          to={category.href}
                                          className="block px-3 py-1.5 text-xs text-primary hover:text-primary/80 transition-colors font-medium cursor-pointer"
                                        >
                                          + {category.subItems.length - 3} {language === 'en' ? 'more varieties' : 'más variedades'}
                                        </Link>
                                      </DropdownMenuItem>
                                    )}
                                  </div>
                                )}
                              </DropdownMenuGroup>
                              {index < item.submenu.length - 1 && <DropdownMenuSeparator />}
                            </div>
                          ))}
                          <DropdownMenuSeparator />
                          <div className="p-3 bg-muted/30 rounded-b-lg">
                            <div className="text-xs text-muted-foreground mb-2">
                              {language === 'en' ? 'Need help choosing?' : '¿Necesita ayuda para elegir?'}
                            </div>
                            <Button variant="outline" size="sm" className="w-full" asChild>
                              <Link to={`/${language}/quote`}>
                                <Calculator className="mr-2 h-3 w-3" />
                                {language === 'en' ? 'Get Free Quote' : 'Cotización Gratis'}
                              </Link>
                            </Button>
                          </div>
                        </>
                      ) : item.key === 'services' ? (
                        <>
                          <DropdownMenuLabel className="text-primary font-semibold">
                            {language === 'en' ? 'Our Services' : 'Nuestros Servicios'}
                          </DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {item.submenu.map((service: any) => (
                            <DropdownMenuItem key={service.key} asChild>
                              <Link
                                to={service.href}
                                className="flex items-center space-x-3 px-3 py-3 cursor-pointer"
                              >
                                <div className="w-8 h-8 bg-gradient-natural rounded-lg flex items-center justify-center">
                                  <Truck className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <div className="font-medium">{service.label}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {service.key === 'sod-delivery' 
                                      ? (language === 'en' ? 'Professional installation available' : 'Instalación profesional disponible')
                                      : (language === 'en' ? 'Delivery available throughout Montgomery County' : 'Entrega disponible en todo el Condado Montgomery')
                                    }
                                  </div>
                                </div>
                              </Link>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <div className="p-3 bg-muted/30 rounded-b-lg">
                            <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-2">
                              <Clock className="h-3 w-3" />
                              <span>{language === 'en' ? 'Service Hours:' : 'Horario de Servicio:'}</span>
                            </div>
                            <div className="text-xs text-foreground">
                              {language === 'en' ? 'Mon-Fri: 7:30AM-4:30PM, Sat: 8AM-12:30PM' : 'Lun-Vie: 7:30AM-4:30PM, Sáb: 8AM-12:30PM'}
                            </div>
                          </div>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    to={item.href}
                    className="flex items-center text-foreground hover:text-primary transition-colors py-2 px-3"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons & Language Toggle */}
          <div className="flex items-center space-x-3">
            <Link to={`/${language}/quote`} aria-label={language === 'en' ? 'Get a free quote' : 'Obtener una cotización gratis'}>
              <Button variant="hero" size="sm" className="hidden sm:inline-flex">
                <Calculator className="mr-2 h-4 w-4" />
                {t('hero.cta.quote')}
              </Button>
            </Link>
            
            <a href="tel:9363200763" aria-label={language === 'en' ? 'Call us at 936-320-0763' : 'Llámenos al 936-320-0763'}>
              <Button variant="outline" size="sm" className="hidden md:inline-flex text-primary border-primary hover:bg-primary hover:text-white">
                <Phone className="mr-2 h-4 w-4" />
                {t('hero.cta.call')}
              </Button>
            </a>

            {/* Language Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center space-x-1"
                >
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">{language.toUpperCase()}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                className="w-56 bg-background/95 backdrop-blur-sm border border-border shadow-glow z-[60]"
                align="end"
              >
                <DropdownMenuLabel className="text-primary font-semibold">
                  {language === 'en' ? 'Select Language' : 'Seleccionar Idioma'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => language !== 'en' && toggleLanguage()}
                  className={`cursor-pointer ${language === 'en' ? 'bg-muted' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-4 bg-gradient-to-r from-blue-500 to-red-500 rounded-sm flex items-center justify-center">
                      🇺🇸
                    </div>
                    <div>
                      <div className="font-medium">English</div>
                      <div className="text-xs text-muted-foreground">United States</div>
                    </div>
                  </div>
                  {language === 'en' && <div className="ml-auto w-2 h-2 bg-primary rounded-full"></div>}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => language !== 'es' && toggleLanguage()}
                  className={`cursor-pointer ${language === 'es' ? 'bg-muted' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-4 bg-gradient-to-r from-red-500 to-yellow-500 rounded-sm flex items-center justify-center">
                      🇪🇸
                    </div>
                    <div>
                      <div className="font-medium">Español</div>
                      <div className="text-xs text-muted-foreground">España</div>
                    </div>
                  </div>
                  {language === 'es' && <div className="ml-auto w-2 h-2 bg-primary rounded-full"></div>}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="p-3 bg-muted/30 rounded-b-lg">
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    <span>{language === 'en' ? 'Serving Montgomery County, TX' : 'Sirviendo al Condado Montgomery, TX'}</span>
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center space-x-1"
                  >
                    <User className="h-4 w-4" />
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  className="w-56 bg-background/95 backdrop-blur-sm border border-border shadow-glow z-[60]"
                  align="end"
                >
                  <DropdownMenuLabel className="text-primary font-semibold">
                    {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => signOut()}
                    className="cursor-pointer text-red-600 hover:text-red-700"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {language === 'en' ? 'Sign Out' : 'Cerrar Sesión'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to={`/${language}/auth`} aria-label={language === 'en' ? 'Sign in to your account' : 'Iniciar sesión en su cuenta'}>
                <Button variant="outline" size="sm" className="hidden sm:inline-flex">
                  <LogIn className="mr-2 h-4 w-4" />
                  {language === 'en' ? 'Sign In' : 'Iniciar Sesión'}
                </Button>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-border bg-card">
            <nav className="py-4 space-y-2">
              {menuItems.map((item) => (
                <div key={item.key}>
                  <Link
                    to={item.href}
                    className={`block px-4 py-2 text-foreground hover:bg-muted transition-colors ${item.key === 'blog' ? 'flex items-center gap-2 bg-primary/5 font-medium' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.key === 'blog' && <FileText className="h-4 w-4 text-primary" />}
                    {item.label}
                    {item.key === 'blog' && (
                      <span className="ml-auto text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                        {language === 'en' ? 'New' : 'Nuevo'}
                      </span>
                    )}
                  </Link>
                  {item.submenu && (
                    <div className="ml-4 space-y-1">
                      {item.key === 'products' ? (
                        // Products mobile submenu
                        item.submenu.map((category: any) => (
                          <div key={category.key} className="space-y-1">
                            <Link
                              to={category.href}
                              className="block px-4 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors border-l-2 border-primary/20"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {category.label}
                            </Link>
                            {category.subItems && (
                              <div className="ml-4 space-y-1">
                                {category.subItems.slice(0, 2).map((product: string) => (
                                  <Link
                                    key={product}
                                    to={`${category.href}/${product.toLowerCase().replace(/\s+/g, '-')}`}
                                    className="block px-4 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    {product}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        // Regular mobile submenu
                        item.submenu.map((subItem: any) => (
                          <Link
                            key={typeof subItem === 'string' ? subItem : subItem.key}
                            to={typeof subItem === 'string' ? `${item.href}/${subItem.toLowerCase().replace(/\s+/g, '-')}` : subItem.href}
                            className="block px-4 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {typeof subItem === 'string' ? subItem : subItem.label}
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              <div className="px-4 pt-4 space-y-2">
                <Link to={`/${language}/quote`} className="block">
                  <Button variant="hero" className="w-full" onClick={() => setIsMenuOpen(false)}>
                    <Calculator className="mr-2 h-4 w-4" />
                    {t('hero.cta.quote')}
                  </Button>
                </Link>
                <a href="tel:9363200763" className="block">
                  <Button variant="outline" className="w-full">
                    <Phone className="mr-2 h-4 w-4" />
                    {t('hero.cta.call')}
                  </Button>
                </a>
                
                {/* Mobile Auth Button */}
                {user ? (
                  <Button 
                    variant="ghost" 
                    className="w-full text-red-600" 
                    onClick={() => {
                      signOut();
                      setIsMenuOpen(false);
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {language === 'en' ? 'Sign Out' : 'Cerrar Sesión'}
                  </Button>
                ) : (
                  <Link to={`/${language}/auth`} className="block">
                    <Button variant="outline" className="w-full" onClick={() => setIsMenuOpen(false)}>
                      <LogIn className="mr-2 h-4 w-4" />
                      {language === 'en' ? 'Sign In' : 'Iniciar Sesión'}
                    </Button>
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;